/* eslint-disable max-lines-per-function */
import { ReactNode, useState, useMemo, useEffect } from 'react';
import { CalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { useParams } from 'react-router-dom';
import { YearMonthParams } from '@/domain/termine/type';
import dayjs from 'dayjs';
import { useTagesDaten } from '@/domain/termine/hooks/useTagesDaten';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { generateWiederholungen } from '@/domain/termine/shared/utils/generateWiederholungen';
import { Termin } from '@/domain/termine/shared/types/Termin';
import { useTerminKalender } from '@/domain/termine/hooks/useTerminKalender';

interface CalendarProviderProps {
  children: ReactNode;
}

function useCalendarDateRange() {
  const { year, month } = useParams<YearMonthParams>();

  const monthDate = useMemo(
    () => ({
      year: parseInt(year ?? `${new Date().getFullYear()}`),
      month: parseInt(month ?? `${new Date().getMonth() + 1}`),
    }),
    [year, month],
  );

  const firstDateOfDisplayedMonth = dayjs(
    `${monthDate.year}-${monthDate.month}`,
  );

  const startDate = firstDateOfDisplayedMonth.startOf('month').startOf('week');
  const endDate = firstDateOfDisplayedMonth.endOf('month').endOf('week');

  return { monthDate, startDate, endDate };
}

function useTermineWithRecurrences(
  termine: Termin[],
  standortId: string | undefined,
  selectedKalenderIds: string[],
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
) {
  return useMemo(() => {
    const standortTermine = standortId
      ? termine.filter((t) => t.standortId === standortId)
      : termine;
    const filteredTermineByStandortAndKalender =
      selectedKalenderIds.length > 0
        ? standortTermine.filter((t) =>
            t.idTerminkalender
              ? selectedKalenderIds.includes(t.idTerminkalender)
              : selectedKalenderIds.includes('NO_KALENDER'),
          )
        : [];
    return filteredTermineByStandortAndKalender.flatMap((termin) =>
      generateWiederholungen(termin, startDate, endDate),
    );
  }, [termine, standortId, selectedKalenderIds, startDate, endDate]);
}

export function CalendarProvider({ children }: CalendarProviderProps) {
  const { currentStandort } = useStandortContext();
  const { monthDate, startDate, endDate } = useCalendarDateRange();
  const [selectedKalenderIds, setSelectedKalenderIds] = useState<string[]>([]);
  const [hasNoKalenderIds, setHastNoKalenderIds] = useState(false);

  const { termine, feiertage } = useTagesDaten([currentStandort.id], {
    von: startDate.format('YYYY-MM-DD'),
    bis: endDate.format('YYYY-MM-DD'),
  });

  const termineWithRecurrences = useTermineWithRecurrences(
    termine,
    currentStandort.id,
    selectedKalenderIds,
    startDate,
    endDate,
  );

  const termineByDay = useMemo(() => {
    const map = new Map<string, Termin[]>();
    termineWithRecurrences.forEach((termin) => {
      const key = dayjs(termin.datum).format('YYYY-MM-DD');
      const existing = map.get(key) ?? [];
      map.set(key, [...existing, termin]);
    });
    return map;
  }, [termineWithRecurrences]);

  const terminKalender = useTerminKalender(
    currentStandort.id ? [currentStandort.id] : [],
    {
      von: startDate.format('YYYY-MM-DD'),
      bis: endDate.format('YYYY-MM-DD'),
    },
  );
  const kalender = terminKalender.kalender;

  const kalenderIds = useMemo(() => kalender.map((k) => k.id), [kalender]);

  useEffect(() => {
    if (!hasNoKalenderIds && kalenderIds.length > 0)
      setSelectedKalenderIds(kalenderIds);
    setHastNoKalenderIds(true);
  }, [kalenderIds, hasNoKalenderIds]);

  const value = useMemo(
    () => ({
      feiertage,
      kalender,
      standortId: currentStandort.id,
      startDate,
      endDate,
      monthDate,
      termineWithRecurrences,
      termineByDay,
      selectedKalenderIds,
      setSelectedKalenderIds,
    }),
    [
      feiertage,
      kalender,
      currentStandort.id,
      startDate,
      endDate,
      monthDate,
      termineWithRecurrences,
      termineByDay,
      setSelectedKalenderIds,
      selectedKalenderIds,
    ],
  );

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}
